import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, TicketPlus, ChevronLeft, ChevronRight, LayoutGrid, Table2, UserCheck, RefreshCw, Filter, FilterIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { format, startOfDay, endOfDay, subHours, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTickets, startTicketsPolling, stopTicketsPolling } from '../store/ticketsSlice';
import { fetchProducts, fetchPriorities, fetchCategories, fetchIssueTypes, fetchUsers } from '../store/entitiesSlice';
import {
  setSearch,
  setStatusFilter,
  setPriorityFilter,
  setProductFilter,
  setAssignedToFilter,
  setCreatedFrom,
  setCreatedRange,
  setCreatedTo,
  setCurrentPage,
  setViewMode,
  toggleSelectTicket,
  setSelectedTickets,
  setBulkAssignOpen,
  setBulkAssignee,
  setBulkStatus,
  clearSelectedTickets,
} from '../store/uiSlice';

const TicketsList = ({ myTickets = false }) => {
  const { user } = useAuth();
  const { sidebarCollapsed } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const paginationMarginLeft = sidebarCollapsed ? '80px' : '220px';

  
  const products = useSelector((s) => s.entities.products);
  const priorities = useSelector((s) => s.entities.priorities);
  const categories = useSelector((s) => s.entities.categories);
  const issueTypes = useSelector((s) => s.entities.issueTypes);
  const users = useSelector((s) => s.entities.users);

  const listKey = myTickets ? `mine:${user?.id || 'unknown'}` : 'all';
  const listState = useSelector((s) => (s.tickets.lists && s.tickets.lists[listKey]) || { items: [], total: 0, loading: false });
  const tickets = listState.items || [];
  const loading = listState.loading;
  const totalTickets = listState.total || 0;

  const uiList = useSelector((s) => (s.ui.perList && s.ui.perList[listKey]) || {
    search: s.ui.search,
    statusFilter: s.ui.statusFilter,
    priorityFilter: s.ui.priorityFilter,
    assignedToFilter: s.ui.assignedToFilter,
    createdFrom: s.ui.createdFrom,
    createdRange: s.ui.createdRange,
    createdTo: s.ui.createdTo,
    productFilter: s.ui.productFilter,
    currentPage: s.ui.currentPage,
    itemsPerPage: s.ui.itemsPerPage,
    viewMode: s.ui.viewMode,
    selectedTickets: s.ui.selectedTickets,
    bulkAssignOpen: s.ui.bulkAssignOpen,
    bulkAssignee: s.ui.bulkAssignee,
    bulkStatus: s.ui.bulkStatus,
  });
  const search = uiList.search;
  const statusFilter = uiList.statusFilter;
  const priorityFilter = uiList.priorityFilter;
  const assignedToFilter = uiList.assignedToFilter;
  const createdFrom = uiList.createdFrom;
  const createdRange = uiList.createdRange;
  const createdTo = uiList.createdTo;
  const productFilter = uiList.productFilter;
  const currentPage = uiList.currentPage;
  const itemsPerPage = uiList.itemsPerPage;
  const viewMode = uiList.viewMode;
  const selectedTickets = uiList.selectedTickets;
  const bulkAssignOpen = uiList.bulkAssignOpen;
  const bulkAssignee = uiList.bulkAssignee;
  const bulkStatus = uiList.bulkStatus;

  // load entities once
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchPriorities());
    dispatch(fetchUsers());
    dispatch(fetchCategories());
    dispatch(fetchIssueTypes());
  }, [dispatch]);

  // build params from ui state
  const buildParams = () => {
    const params = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
    };
    if (myTickets) {
      params.assigned_to_id = user?.id;
    } else if (assignedToFilter && assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned') {
        params.assigned_unassigned = true;
      } else {
        params.assigned_to_id = assignedToFilter;
      }
    }
    if (search) params.search = search;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (priorityFilter && priorityFilter !== 'all') params.priority_id = priorityFilter;
    if (productFilter && productFilter !== 'all') params.product_id = productFilter;
    // handle created date presets or custom range
    if (createdRange && createdRange !== 'custom' && createdRange !== 'all') {
      let fromDate, toDate;
      const now = new Date();
      switch (createdRange) {
        case 'today':
          fromDate = startOfDay(now);
          toDate = endOfDay(now);
          break;
        case 'yesterday':
          fromDate = startOfDay(subDays(now, 1));
          toDate = endOfDay(subDays(now, 1));
          break;
        case 'last_24hr':
          fromDate = subHours(now, 24);
          toDate = now;
          break;
        case 'this_week':
          fromDate = startOfWeek(now, { weekStartsOn: 1 });
          toDate = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'this_month':
          fromDate = startOfMonth(now);
          toDate = endOfMonth(now);
          break;
        default:
          fromDate = null;
          toDate = null;
      }
      if (fromDate) params.created_from = format(fromDate, 'yyyy-MM-dd');
      if (toDate) params.created_to = format(toDate, 'yyyy-MM-dd');
    } else {
      if (createdFrom) params.created_from = createdFrom;
      if (createdTo) params.created_to = createdTo;
    }
    return params;
  };

  // build params from an explicit set of values (used when applying a filter immediately)
  const buildParamsFromValues = (vals) => {
    const {
      search: vSearch,
      statusFilter: vStatus,
      priorityFilter: vPriority,
      productFilter: vProduct,
      assignedToFilter: vAssigned,
      createdRange: vRange,
      createdFrom: vFrom,
      createdTo: vTo,
      currentPage: vPage,
      itemsPerPage: vLimit,
    } = vals;

    const params = {
      skip: ( (vPage || 1) - 1) * (vLimit || itemsPerPage),
      limit: vLimit || itemsPerPage,
    };
    if (myTickets) {
      params.assigned_to_id = user?.id;
    } else if (vAssigned && vAssigned !== 'all') {
      if (vAssigned === 'unassigned') {
        params.assigned_unassigned = true;
      } else {
        params.assigned_to_id = vAssigned;
      }
    }
    if (vSearch) params.search = vSearch;
    if (vStatus && vStatus !== 'all') params.status = vStatus;
    if (vPriority && vPriority !== 'all') params.priority_id = vPriority;
    if (vProduct && vProduct !== 'all') params.product_id = vProduct;

    // created range handling (same logic as buildParams)
    if (vRange && vRange !== 'custom' && vRange !== 'all') {
      let fromDate, toDate;
      const now = new Date();
      switch (vRange) {
        case 'today':
          fromDate = startOfDay(now);
          toDate = endOfDay(now);
          break;
        case 'yesterday':
          fromDate = startOfDay(subDays(now, 1));
          toDate = endOfDay(subDays(now, 1));
          break;
        case 'last_24hr':
          fromDate = subHours(now, 24);
          toDate = now;
          break;
        case 'this_week':
          fromDate = startOfWeek(now, { weekStartsOn: 1 });
          toDate = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'this_month':
          fromDate = startOfMonth(now);
          toDate = endOfMonth(now);
          break;
        default:
          fromDate = null;
          toDate = null;
      }
      if (fromDate) params.created_from = format(fromDate, 'yyyy-MM-dd');
      if (toDate) params.created_to = format(toDate, 'yyyy-MM-dd');
    } else {
      if (vFrom) params.created_from = vFrom;
      if (vTo) params.created_to = vTo;
    }

    return params;
  };

  // apply a UI change immediately: update store, reset page, fetch with merged params and restart poller
  const applyAndFetch = (updates = {}) => {
    // dispatch set actions for provided keys
    if ('search' in updates) dispatch(setSearch({ listKey, value: updates.search }));
    if ('statusFilter' in updates) dispatch(setStatusFilter({ listKey, value: updates.statusFilter }));
    if ('priorityFilter' in updates) dispatch(setPriorityFilter({ listKey, value: updates.priorityFilter }));
    if ('productFilter' in updates) dispatch(setProductFilter({ listKey, value: updates.productFilter }));
    if ('assignedToFilter' in updates) dispatch(setAssignedToFilter({ listKey, value: updates.assignedToFilter }));
    if ('createdRange' in updates) dispatch(setCreatedRange({ listKey, value: updates.createdRange }));
    if ('createdFrom' in updates) dispatch(setCreatedFrom({ listKey, value: updates.createdFrom }));
    if ('createdTo' in updates) dispatch(setCreatedTo({ listKey, value: updates.createdTo }));

    // reset to first page on filter change
    dispatch(setCurrentPage({ listKey, value: 1 }));

    // build params using merged values (prefer updates over current selectors)
    const merged = {
      search,
      statusFilter,
      priorityFilter,
      productFilter,
      assignedToFilter,
      createdRange,
      createdFrom,
      createdTo,
      currentPage: 1,
      itemsPerPage,
      ...updates,
    };

    const params = buildParamsFromValues(merged);
    dispatch(fetchTickets({ params, listKey }));
    dispatch(startTicketsPolling({ intervalMs: 30000, params, listKey }));
  };

  // fetch tickets when filters/page change
  useEffect(() => {
    const params = buildParams();
    dispatch(fetchTickets({ params, listKey }));
    // also restart poller with latest params
    dispatch(startTicketsPolling({ intervalMs: 30000, params, listKey }));
    return () => dispatch(stopTicketsPolling(listKey));
  }, [dispatch, myTickets, currentPage, search, statusFilter, priorityFilter, productFilter, assignedToFilter, createdFrom, createdTo, createdRange, user]);

  const handleSelectTicket = (ticketId) => {
    dispatch(toggleSelectTicket({ listKey, id: ticketId }));
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      dispatch(setSelectedTickets({ listKey, value: [] }));
    } else {
      dispatch(setSelectedTickets({ listKey, value: tickets.map(t => t.id) }));
    }
  };

  const handleBulkAssign = async () => {
    try {
      const updates = selectedTickets.map(ticketId => {
        const payload = {};
        if (bulkAssignee && bulkAssignee !== 'none') payload.assigned_to_id = parseInt(bulkAssignee);
        if (bulkStatus && bulkStatus !== 'none') payload.status = bulkStatus;
        return api.put(`/tickets/${ticketId}`, payload);
      });
      await Promise.all(updates);
      toast.success(`${selectedTickets.length} ticket(s) updated successfully`);
      dispatch(setBulkAssignOpen({ listKey, value: false }));
      dispatch(clearSelectedTickets({ listKey }));
      dispatch(setBulkAssignee({ listKey, value: '' }));
      dispatch(setBulkStatus({ listKey, value: '' }));
      // refresh
      dispatch(fetchTickets({ params: buildParams(), listKey }));
    } catch (error) {
      toast.error('Failed to update tickets');
    }
  };

  // ticket fetching handled by store thunks

  // entities handled by entities slice

  const lookup = (list, id, field = 'name') => {
    if (!id && id !== 0) return '—';
    // id might be an object or primitive
    const needle = typeof id === 'object' && id !== null ? id.id || id : id;
    const item = Array.isArray(list) ? list.find(x => String(x.id) === String(needle)) : null;
    return item ? (item[field] || item.name || '—') : (typeof id === 'string' || typeof id === 'number' ? String(id) : '—');
  };

  const getUser = (ticket) => {
    // created_by may be nested object or id
    if (ticket.created_by && typeof ticket.created_by === 'object') return ticket.created_by.full_name || ticket.created_by.username || ticket.created_by.name || '—';
    const id = ticket.created_by_id || ticket.created_by;
    const u = users.find(x => String(x.id) === String(id));
    return u ? (u.full_name || u.username || u.name) : (ticket.created_by || '—');
  };

  const getAssignee = (ticket) => {
    if (ticket.assigned_to && typeof ticket.assigned_to === 'object') return ticket.assigned_to.full_name || ticket.assigned_to.username || ticket.assigned_to.name || 'Unassigned';
    const id = ticket.assigned_to_id || ticket.assigned_to;
    const u = users.find(x => String(x.id) === String(id));
    return u ? (u.full_name || u.username || u.name) : (ticket.assigned_to ? String(ticket.assigned_to) : 'Unassigned');
  };

  const handleSearch = () => {
    dispatch(setCurrentPage({ listKey, value: 1 }));
    dispatch(fetchTickets({ params: buildParams(), listKey }));
  };

  // prevent future dates and ensure from <= to; if invalid, adjust the opposite date
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const handleCreatedFromChange = (value) => {
    // allow clearing
    if (!value) {
      applyAndFetch({ createdFrom: '' });
      return;
    }
    // clamp to today if a future date selected
    const v = value > todayStr ? todayStr : value;
    const updates = { createdFrom: v };
    // if to exists and is before from, move to to = from
    if (createdTo && createdTo !== '' && v > createdTo) {
      updates.createdTo = v;
    }
    applyAndFetch(updates);
  };

  const handleCreatedToChange = (value) => {
    if (!value) {
      applyAndFetch({ createdTo: '' });
      return;
    }
    const v = value > todayStr ? todayStr : value;
    const updates = { createdTo: v };
    // if from exists and is after to, move from = to
    if (createdFrom && createdFrom !== '' && v < createdFrom) {
      updates.createdFrom = v;
    }
    applyAndFetch(updates);
  };

  const handleResetFilters = () => {
    // reset per-list UI filters to defaults
    dispatch(setSearch({ listKey, value: '' }));
    dispatch(setStatusFilter({ listKey, value: 'all' }));
    dispatch(setPriorityFilter({ listKey, value: 'all' }));
    dispatch(setProductFilter({ listKey, value: 'all' }));
    dispatch(setAssignedToFilter({ listKey, value: 'all' }));
    dispatch(setCreatedRange({ listKey, value: 'all' }));
    dispatch(setCreatedFrom({ listKey, value: '' }));
    dispatch(setCreatedTo({ listKey, value: '' }));
    dispatch(setCurrentPage({ listKey, value: 1 }));
    dispatch(clearSelectedTickets({ listKey }));
    dispatch(setBulkAssignee({ listKey, value: '' }));
    dispatch(setBulkStatus({ listKey, value: '' }));
    // immediate refresh using basic params (avoid reading stale selector values)
    const params = buildParamsFromValues({
      search: '',
      statusFilter: 'all',
      priorityFilter: 'all',
      productFilter: 'all',
      assignedToFilter: myTickets ? user?.id : 'all',
      createdRange: 'all',
      createdFrom: '',
      createdTo: '',
      currentPage: 1,
      itemsPerPage,
    });
    dispatch(fetchTickets({ params, listKey }));
    dispatch(startTicketsPolling({ intervalMs: 30000, params, listKey }));
    // polling effect will restart via existing effect when selectors change
  };

  const totalPages = Math.max(1, Math.ceil(totalTickets / itemsPerPage));

  const statusColors = {
    'Open': 'bg-amber-50 text-amber-700 border-amber-200',
    'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
    'Resolved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Closed': 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <div className="space-y-4 md:space-y-6 dark:bg-slate-950" data-testid={myTickets ? 'my-tickets-page' : 'tickets-page'}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
            {myTickets ? 'My Tickets' : 'All Tickets'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
            {myTickets ? 'Tickets assigned to you' : 'View and manage all tickets'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {selectedTickets.length > 0 && (
            <Button
              onClick={() => dispatch(setBulkAssignOpen({ listKey, value: true }))}
              variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-600 dark:text-indigo-300 dark:hover:bg-indigo-900/30 text-xs sm:text-sm"
            >
              <UserCheck className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Assign {selectedTickets.length}
            </Button>
          )}
          <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-700 dark:bg-slate-900/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => dispatch(setViewMode({ listKey, value: 'card' }))}
              className={viewMode === 'card' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'h-8 w-8 p-0 dark:text-slate-400 dark:hover:text-white'}
            >
              <LayoutGrid size={16} />
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => dispatch(setViewMode({ listKey, value: 'table' }))}
              className={viewMode === 'table' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'h-8 w-8 p-0 dark:text-slate-400 dark:hover:text-white'}
            >
              <Table2 size={16} />
            </Button>
          </div>
          <Button
            onClick={() => navigate('/create-ticket')}
            data-testid="create-ticket-btn"
            className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium shadow-md shadow-indigo-500/25 text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <TicketPlus className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Create
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 dark:bg-slate-900/80 dark:border-slate-800 shadow-sm">
        <CardContent className="p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2.5 sm:gap-3 md:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search tickets..."
                data-testid="search-input"
                value={search}
                onChange={(e) => dispatch(setSearch({ listKey, value: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:placeholder-slate-500 focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm"
              />
            </div>

            <Select value={statusFilter} onValueChange={(v) => applyAndFetch({ statusFilter: v })}>
              <SelectTrigger data-testid="status-filter" className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={(v) => applyAndFetch({ priorityFilter: v })}>
              <SelectTrigger data-testid="priority-filter" className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {priorities.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={productFilter} onValueChange={(v) => applyAndFetch({ productFilter: v })}>
              <SelectTrigger data-testid="product-filter" className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                <SelectValue placeholder="Product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!myTickets && (
              <Select value={assignedToFilter} onValueChange={(v) => applyAndFetch({ assignedToFilter: v })}>
                <SelectTrigger data-testid="assigned-filter" className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                  <SelectValue placeholder="Assigned To" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.filter(u => u.is_active).map((u) => (
                    <SelectItem key={u.id} value={u.id.toString()}>{u.full_name || u.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={createdRange} onValueChange={(v) => applyAndFetch({ createdRange: v })}>
              <SelectTrigger data-testid="created-range" className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white focus:border-indigo-500 focus:ring-indigo-500 text-xs sm:text-sm">
                <SelectValue placeholder="Created" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last_24hr">Last 24hr</SelectItem>
                <SelectItem value="this_week">This week</SelectItem>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>  
              </SelectContent>
            </Select>

            {createdRange === 'custom' && (
              <>
                <div className="w-full space-y-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">From</span>
                    <Input
                      type="date"
                      value={createdFrom || ''}
                      onChange={(e) => handleCreatedFromChange(e.target.value)}
                      className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white w-full pl-14"
                      max={todayStr}
                      aria-label="Created from"
                      data-testid="created-from"
                    />
                  </div>
                </div>
                <div className="w-full space-y-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">To</span>
                    <Input
                      type="date"
                      value={createdTo || ''}
                      onChange={(e) => handleCreatedToChange(e.target.value)}
                      className="border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:text-white w-full pl-14"
                      max={todayStr}
                      aria-label="Created to"
                      data-testid="created-to"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={handleSearch} data-testid="apply-filters-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white" aria-label="Apply filters">
                      <FilterIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Apply filters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button onClick={handleResetFilters} data-testid="reset-filters-btn" variant="outline" className="border-slate-300 text-slate-700" aria-label="Reset filters">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset filters</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card className="border-slate-200 dark:bg-slate-900/80 dark:border-slate-800 shadow-sm">
        <CardContent className="p-6 pb-12 bg-white dark:bg-slate-900/80">
          {loading ? (
            <div className="space-y-3">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="p-5 border border-slate-200 rounded-xl">
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <TicketPlus className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No tickets found</p>
              <p className="text-slate-500 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : viewMode === 'card' ? (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const priority = priorities.find(p => p.id === ticket.priority_id);
                return (
                  <div
                    key={ticket.id}
                    data-testid={`ticket-${ticket.id}`}
                    className="group p-5 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer bg-white"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-sm font-mono font-semibold text-indigo-600">
                            {ticket.ticket_id}
                          </span>
                          <Badge className={`${statusColors[ticket.status]} border px-2.5 py-0.5`}>
                            {ticket.status}
                          </Badge>
                          {priority && (
                            <Badge
                              style={{
                                backgroundColor: priority.color + '15',
                                color: priority.color,
                                borderColor: priority.color + '40'
                              }}
                              className="border px-2.5 py-0.5 font-medium"
                            >
                              {priority.name}
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1 group-hover:text-indigo-600 transition-colors">
                          {ticket.title}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Created <strong>{ticket.created_at ? format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm') : '—'}</strong> by <strong>{getUser(ticket)}</strong> • Updated: <strong>{ticket.updated_at ? format(new Date(ticket.updated_at), 'MMM dd, yyyy HH:mm') : '—'}</strong> • Assigned: <strong>{getAssignee(ticket)}</strong>
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {/* Product / Category / Issue as tag-like badges beside the priority */}
                          <Badge className="border px-2 py-0.5 text-xs font-medium">{lookup(products, ticket.product_id || ticket.product)}</Badge>
                          <Badge className="border px-2 py-0.5 text-xs font-medium">{lookup(categories, ticket.category_id || ticket.category)}</Badge>
                          <Badge className="border px-2 py-0.5 text-xs font-medium">{lookup(issueTypes, ticket.issue_type_id || ticket.issue_type)}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="relative">
              <div className="overflow-x-auto">
                <table className="w-full text-slate-900 dark:text-slate-200">
                <thead className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 px-2.5 text-xs">
                      <Checkbox
                        checked={selectedTickets.length === tickets.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 min-w-24">Ticket ID</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Title</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Product</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Category</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Issue</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Status</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Priority</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Created At</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Created By</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Updated At</th>
                    <th className="text-left py-2 px-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">Assigned</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-900/50">
                  {tickets.map((ticket) => {
                    const priority = priorities.find(p => p.id === ticket.priority_id);
                    return (
                      <tr
                        key={ticket.id}
                        data-testid={`ticket-${ticket.id}`}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/50 dark:hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="py-2 px-2.5" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedTickets.includes(ticket.id)}
                            onCheckedChange={() => handleSelectTicket(ticket.id)}
                          />
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer min-w-24 whitespace-nowrap" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs font-mono font-semibold text-indigo-600">
                            {ticket.ticket_id}
                          </span>
                        </td>
                        <td className="py-2 px-2.5 max-w-md cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <div className="text-xs font-medium text-slate-900 dark:text-white line-clamp-2">
                            {ticket.title}
                          </div>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-700 dark:text-slate-300">{lookup(products, ticket.product_id || ticket.product)}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-700 dark:text-slate-300">{lookup(categories, ticket.category_id || ticket.category)}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-700 dark:text-slate-300">{lookup(issueTypes, ticket.issue_type_id || ticket.issue_type)}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <Badge className={`${statusColors[ticket.status]} border px-2 py-0.5 text-xs`}>
                            {ticket.status}
                          </Badge>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          {priority && (
                            <Badge
                              style={{
                                backgroundColor: priority.color + '15',
                                color: priority.color,
                                borderColor: priority.color + '40'
                              }}
                              className="border px-2 py-0.5 font-medium text-xs"
                            >
                              {priority.name}
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{ticket.created_at ? format(new Date(ticket.created_at), 'MMM dd HH:mm') : '—'}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-700 dark:text-slate-300">{getUser(ticket)}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{ticket.updated_at ? format(new Date(ticket.updated_at), 'MMM dd HH:mm') : '—'}</span>
                        </td>
                        <td className="py-2 px-2.5 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                          <span className="text-xs text-slate-700 dark:text-slate-300">{getAssignee(ticket)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination - Fixed at Page Bottom */}
      {!loading && totalTickets > 0 && (
        <div className="fixed bottom-0 right-0 left-0 z-50 bg-white dark:bg-slate-900 flex items-center justify-between px-6 py-3 border-t border-slate-200 dark:border-slate-700 shadow-lg" style={{marginLeft: paginationMarginLeft}}>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTickets)} of {totalTickets} tickets
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch(setCurrentPage({ listKey, value: Math.max(1, currentPage - 1) }))}
              disabled={currentPage === 1}
              className="border-slate-300 h-7 px-2 text-xs"
            >
              <ChevronLeft className="h-3 w-3" />
              Prev
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => dispatch(setCurrentPage({ listKey, value: pageNum }))}
                  className={currentPage === pageNum ? "bg-indigo-600 hover:bg-indigo-700 text-white h-7 px-2 text-xs" : "border-slate-300 h-7 px-2 text-xs"}
                >
                  {pageNum}
                </Button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-1 text-slate-500 text-xs">...</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dispatch(setCurrentPage({ listKey, value: totalPages }))}
                  className="border-slate-300 h-7 px-2 text-xs"
                >
                  {totalPages}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch(setCurrentPage({ listKey, value: Math.min(totalPages, currentPage + 1) }))}
              disabled={currentPage === totalPages}
              className="border-slate-300 h-7 px-2 text-xs"
            >
              Next
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Assign Dialog */}
          <Dialog open={bulkAssignOpen} onOpenChange={(open) => dispatch(setBulkAssignOpen({ listKey, value: open }))}>
        <DialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Bulk Assign Tickets</DialogTitle>
            <DialogDescription>
              Assign {selectedTickets.length} selected ticket(s) to a user and/or change status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={bulkAssignee} onValueChange={(v) => dispatch(setBulkAssignee({ listKey, value: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Change</SelectItem>
                  {users.filter(u => u.role !== 'Client' && u.is_active).map(u => (
                    <SelectItem key={u.id} value={u.id.toString()}>
                      {u.full_name || u.username} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={bulkStatus} onValueChange={(v) => dispatch(setBulkStatus({ listKey, value: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Change</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => dispatch(setBulkAssignOpen({ listKey, value: false }))}>Cancel</Button>
            <Button onClick={handleBulkAssign} className="bg-indigo-600 hover:bg-indigo-700">
              Update Tickets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketsList;
